import { gql } from '@graphql/__generated__';

export const GET_LINKED_LISTING_IDS_FOR_TEAMS = gql(/* GraphQL */ `
  query GetLinkedListingIdsForTeams($teamIds: [uuid!]!) {
    property_transactions_listings(
      where: {
        property_transaction: { seller_broker_team_id: { _in: $teamIds } }
      }
    ) {
      listing_id
    }
  }
`);

export const GET_PROPERTY_TRANSACTIONS = gql(/* GraphQL */ `
  query getPropertyTransactions(
    $where: property_transactions_bool_exp!
    $order_by: [property_transactions_order_by!]
    $limit: Int!
    $offset: Int!
    $team_ids: [uuid!]!
    $includeValuations: Boolean! = false
  ) {
    property_transactions(
      where: $where
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      ...TransactionsListRow
      reviews(where: { completed: { _eq: true } }) {
        id
        is_buyer
      }
    }
  }
`);

export const GET_PROPERTY_TRANSACTIONS_LIST_COUNT = gql(/* GraphQL */ `
  query getPropertyTransactionsListCount(
    $where: property_transactions_bool_exp!
  ) {
    property_transactions_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_DUPLICATE_TRANSACTIONS = gql(/* GraphQL */ `
  query GetDuplicateTransactions(
    $route: String
    $street_number: String
    $postcode: String
    $locality: String
  ) {
    property_transactions(
      where: {
        property: {
          route: { _eq: $route }
          street_number: { _eq: $street_number }
          postcode: { _eq: $postcode }
          locality: { _eq: $locality }
        }
      }
    ) {
      id
      purchase_price
      signature_date
      property {
        id
        route
        street_number
        postcode
        locality
      }
      seller_broker {
        id
        first_name
        last_name
      }
    }
  }
`);

export const DRAWER_GET_PROPERTY_TRANSACTION = gql(/* GraphQL */ `
  query TransactionDrawerGetTransaction($id: uuid!) {
    property_transactions_by_pk(id: $id) {
      id
      lot_id
      property_id
      buyer_broker_team_id
      seller_broker_team_id
      buyer_broker_id
      seller_broker_id
      created_by
      mls_share {
        id
        team_id
      }
    }
  }
`);

export const DELETE_TRANSACTION = gql(/* GraphQL */ `
  mutation DeleteTransaction($id: uuid!) {
    delete_property_transactions_by_pk(id: $id) {
      id
    }
  }
`);

export const TRANSACTION_FORM_FRAGMENT = gql(/* GraphQL */ `
  fragment TransactionFormFragment on property_transactions {
    id
    created_by
    created_at
    signature_date
    updated_at
    reviewed_at
    reviewed_by
    buyer_broker_id
    seller_broker_id
    buyer_broker_team_id
    seller_broker_team_id
    estimated_signature_date
    payment_date
    purchase_price
    is_published
    lot_id
    lot {
      id
      currency
      property {
        id
        country_code
      }
    }
    sellers {
      transaction_id
      user_id
    }
    buyers {
      transaction_id
      user_id
    }
  }
`);

export const TRANSACTION_COMMISSIONS_FORM_FRAGMENT = gql(/* GraphQL */ `
  fragment TransactionCommissionsFormFragment on property_transactions {
    id
    purchase_price
    commissions(order_by: { payment_date: asc }) {
      id
      type
      amount
      vat
      contact_id
      beneficiary_team_id
    }
    lot {
      id
      currency
      mandate_type
      property {
        id
        country_code
      }
    }
  }
`);

export const TRANSACTION_FORM_QUERY = gql(/* GraphQL */ `
  query TransactionFormQuery($id: uuid!) {
    property_transactions_by_pk(id: $id) {
      id
      ...TransactionFormFragment
    }
  }
`);

export const UPDATE_TRANSACTION_REVIEWED_AT = gql(/* GraphQL */ `
  mutation UpdateTransactionReviewedAt(
    $id: uuid!
    $reviewed_at: timestamptz!
    $reviewed_by: uuid!
  ) {
    update_property_transactions_by_pk(
      pk_columns: { id: $id }
      _set: { reviewed_at: $reviewed_at, reviewed_by: $reviewed_by }
    ) {
      id
      ...TransactionFormFragment
    }
  }
`);

export const TRANSACTION_FORM_UPDATE = gql(/* GraphQL */ `
  mutation TransactionFormUpdate(
    $id: uuid!
    $transaction: property_transactions_set_input!
    $sellers: [transaction_sellers_insert_input!]!
    $buyers: [transaction_buyers_insert_input!]!
  ) {
    update_property_transactions_by_pk(
      pk_columns: { id: $id }
      _set: $transaction
    ) {
      id
      ...TransactionFormFragment
      seller_broker {
        id
        ...UserInfo_user
      }
      buyer_broker {
        id
        ...UserInfo_user
      }
      buyer_broker_team {
        id
        ...TeamInfo_team
      }
      seller_broker_team {
        id
        ...TeamInfo_team
      }
    }
    delete_transaction_buyers(where: { transaction_id: { _eq: $id } }) {
      returning {
        transaction_id
        user_id
      }
    }
    delete_transaction_sellers(where: { transaction_id: { _eq: $id } }) {
      returning {
        transaction_id
        user_id
      }
    }
    insert_transaction_buyers(objects: $buyers) {
      returning {
        transaction_id
        user_id
      }
    }
    insert_transaction_sellers(objects: $sellers) {
      returning {
        transaction_id
        user_id
      }
    }
  }
`);

export const TRANSACTION_COMMISSIONS_FORM_UPDATE = gql(/* GraphQL */ `
  mutation TransactionCommissionsFormUpdate(
    $id: uuid!
    $commissions: [commissions_insert_input!]!
  ) {
    delete_commissions(where: { property_transaction_id: { _eq: $id } }) {
      returning {
        id
        type
        amount
        vat
        contact_id
        beneficiary_team_id
        property_transaction_id
      }
    }
    insert_commissions(objects: $commissions) {
      returning {
        id
        type
        amount
        vat
        contact_id
        beneficiary_team_id
        property_transaction_id
      }
    }
  }
`);

export const CREATE_TRANSACTION = gql(/* GraphQL */ `
  mutation CreateTransaction(
    $transaction: property_transactions_insert_input!
  ) {
    insert_property_transactions_one(object: $transaction) {
      id
      ...TransactionFormFragment
      seller_broker {
        id
        ...UserInfo_user
      }
      buyer_broker {
        id
        ...UserInfo_user
      }
      buyer_broker_team {
        id
        ...TeamInfo_team
      }
      seller_broker_team {
        id
        ...TeamInfo_team
      }
      property {
        id
      }
      buyers {
        user_id
        transaction_id
      }
      sellers {
        user_id
        transaction_id
      }
    }
  }
`);

export const GET_USER_DEFAULT_TEAM_ID = gql(/* GraphQL */ `
  query GetUserDefaultTeamId($id: uuid!) {
    users_by_pk(id: $id) {
      default_team_id
      teams_users(where: { deactivated: { _eq: false } }) {
        team_id
      }
    }
  }
`);

export const GET_TRANSACTION_DOCUMENTS = gql(/* GraphQL */ `
  query GetTransactionDocuments($id: uuid!) {
    property_transactions_by_pk(id: $id) {
      id
      property {
        id
        property_files(order_by: { order_nr: desc }) {
          ...PropertyFile
        }
      }
    }
  }
`);

export const GET_TRANSACTION_PROPERTY = gql(/* GraphQL */ `
  query GetTransactionProperty($id: uuid!) {
    property_transactions_by_pk(id: $id) {
      id
      ...TransactionCard
    }
  }
`);

export const DELETE_PROPERTY_TRANSACTION_LISTING = gql(/* GraphQL */ `
  mutation DeletePropertyTransactionListing($id: uuid!, $listingId: Int!) {
    delete_property_transactions_listings(
      where: {
        property_transaction_id: { _eq: $id }
        listing_id: { _eq: $listingId }
      }
    ) {
      affected_rows
      returning {
        property_transaction_id
        listing_id
      }
    }
  }
`);

export const GET_LINKED_LISTINGS = gql(/* GraphQL */ `
  query GetLinkedListings(
    $ids: [Int!]!
    $ch: Boolean!
    $es: Boolean!
    $fr: Boolean!
    $it: Boolean!
  ) {
    ch_cma_listings(where: { id: { _in: $ids } }) @include(if: $ch) {
      id
      number_of_rooms
      number_of_bathrooms
      computed_surface
      land_surface
      sale_price
      computed_price_per_sqm
      offline_since
      created_at
      valid_from
      country_code
      address
      route
      street_number
      postcode
      locality
      state_id
      visit_contact_person
      agency_contact_person
      agency_name
      places {
        id
        label
      }
      property_type
      images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
    }

    fr_cma_listings(where: { id: { _in: $ids } }) @include(if: $fr) {
      id
      number_of_rooms
      number_of_bathrooms
      computed_surface
      land_surface
      sale_price
      computed_price_per_sqm
      offline_since
      created_at
      valid_from
      country_code
      address
      route
      street_number
      postcode
      locality
      state_id
      visit_contact_person
      agency_contact_person
      agency_name
      places {
        id
        label
      }
      property_type
      images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
    }
    es_cma_listings(where: { id: { _in: $ids } }) @include(if: $es) {
      id
      number_of_rooms
      number_of_bathrooms
      computed_surface
      land_surface
      sale_price
      computed_price_per_sqm
      offline_since
      created_at
      valid_from
      country_code
      address
      route
      street_number
      postcode
      locality
      state_id
      visit_contact_person
      agency_contact_person
      agency_name
      places {
        id
        label
      }
      property_type
      images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
    }
    it_cma_listings(where: { id: { _in: $ids } }) @include(if: $it) {
      id
      number_of_rooms
      number_of_bathrooms
      computed_surface
      land_surface
      sale_price
      computed_price_per_sqm
      offline_since
      created_at
      valid_from
      country_code
      address
      route
      street_number
      postcode
      locality
      state_id
      visit_contact_person
      agency_contact_person
      agency_name
      places {
        id
        label
      }
      property_type
      images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
    }
  }
`);

export const INSERT_PROPERTY_TRANSACTION_LISTINGS = gql(/* GraphQL */ `
  mutation InsertPropertyTransactionListings(
    $objects: [property_transactions_listings_insert_input!]!
  ) {
    insert_property_transactions_listings(
      objects: $objects
      on_conflict: {
        constraint: property_transactions_listings_pkey
        update_columns: [updated_at]
      }
    ) {
      returning {
        property_transaction_id
        property_transaction {
          id
          property_transactions_listings {
            property_transaction_id
            listing_id
          }
        }
        listing_id
      }
    }
  }
`);

export const GET_TRANSACTION_VALUATIONS = gql(/* GraphQL */ `
  query GetTransactionValuations($transactionId: uuid!) {
    transaction_valuations(
      where: { transaction_id: { _eq: $transactionId } }
      order_by: { created_at: desc }
    ) {
      lead_id
      first_name
      last_name
      created_at
      requalified_at
      appraisal_perception
      appraisal_perceived_value
      postcode
      locality
      route
      street_number
      appraisal_min
      appraisal_max
      appraisal_reason
      lead {
        sale_horizon
        buy_horizon
        property {
          property_type {
            main_type
            label
            main_type_object {
              label
            }
            composite_type
            composite_type_object {
              label
            }
          }
        }
      }
    }
  }
`);

export const TRANSACTION_CREATION_FRAGMENT = gql(/* GraphQL */ `
  fragment TransactionCreationFragment on property_transactions {
    id
    created_at
    updated_at
    seller_broker_id
    buyer_broker_id
    seller_broker_team_id
    buyer_broker_team_id
    purchase_price
    signature_date
    estimated_signature_date
    payment_date
    property_id
    property {
      id
      country_code
      route
      street_number
      postcode
      locality
      state
      state_id
      country
      lat
      lng
      property_type_id
      property_type {
        id
        label
        main_type
        main_type_object {
          label
        }
      }
      number_of_rooms
      number_of_bedrooms
      living_surface
      land_surface
      built_surface
      usable_surface
      property_images {
        id
        image_id
        image {
          id
          url
        }
      }
    }
    seller_broker {
      id
      first_name
      last_name
      ...UserInfo_user
    }
    buyer_broker {
      id
      first_name
      last_name
      ...UserInfo_user
    }
    seller_broker_team {
      id
      name
      ...TeamInfo_team
    }
    buyer_broker_team {
      id
      name
      ...TeamInfo_team
    }
  }
`);

export const CREATE_TRANSACTION_WITH_PROPERTY = gql(/* GraphQL */ `
  mutation CreateTransactionWithProperty(
    $transaction: property_transactions_insert_input!
  ) {
    insert_property_transactions_one(object: $transaction) {
      id
      ...TransactionCreationFragment
    }
  }
`);

export const ADD_PROPERTY_IMAGE = gql(/* GraphQL */ `
  mutation AddPropertyImage($property_image: property_images_insert_input!) {
    insert_property_images_one(object: $property_image) {
      id
      property_id
      image_id
      image {
        id
        url
      }
    }
  }
`);

export const BULK_PUBLISH_TRANSACTIONS = gql(/* GraphQL */ `
  mutation BulkPublishTransactions($ids: [uuid!]!) {
    update_property_transactions(
      where: { id: { _in: $ids }, reviewed_at: { _is_null: false } }
      _set: { is_published: true }
    ) {
      affected_rows
      returning {
        id
        is_published
      }
    }
  }
`);

export const BULK_UNPUBLISH_TRANSACTIONS = gql(/* GraphQL */ `
  mutation BulkUnpublishTransactions($ids: [uuid!]!) {
    update_property_transactions(
      where: { id: { _in: $ids } }
      _set: { is_published: false }
    ) {
      affected_rows
      returning {
        id
        is_published
      }
    }
  }
`);

export const GET_BROKER_AND_TEAM_NAMES = gql(/* GraphQL */ `
  query GetBrokerAndTeamNames(
    $broker_id: uuid!
    $team_id: uuid!
    $include_team: Boolean!
  ) {
    broker: users_by_pk(id: $broker_id) {
      id
      first_name
      last_name
      full_name
    }
    team: teams_by_pk(id: $team_id) @include(if: $include_team) {
      id
      name
    }
  }
`);
